import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { format } from 'date-fns';

const { FiTrash2, FiEdit, FiCopy, FiCalendar } = FiIcons;

const SavedPrompts = ({ prompts, onLoad, onDelete }) => {
  const copyToClipboard = async (prompt) => {
    try {
      await navigator.clipboard.writeText(prompt);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  if (prompts.length === 0) {
    return (
      <div className="text-center py-12">
        <SafeIcon icon={FiEdit} className="w-16 h-16 text-slate-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-slate-600 mb-2">No saved prompts</h3>
        <p className="text-slate-500">Create and save prompts to see them here</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold text-slate-800 mb-6">Saved Prompts</h2>
      
      {prompts.map((prompt, index) => (
        <motion.div
          key={prompt.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.1 }}
          className="bg-white rounded-lg border border-slate-200 p-6 hover:shadow-md transition-shadow"
        >
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="text-lg font-medium text-slate-800">{prompt.name}</h3>
              <div className="flex items-center space-x-2 text-sm text-slate-500 mt-1">
                <SafeIcon icon={FiCalendar} className="w-4 h-4" />
                <span>{format(new Date(prompt.createdAt), 'MMM d, yyyy')}</span>
              </div>
            </div>
            <div className="flex space-x-2">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => copyToClipboard(prompt.prompt)}
                className="p-2 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              >
                <SafeIcon icon={FiCopy} className="w-4 h-4" />
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => onLoad(prompt)}
                className="p-2 text-slate-500 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
              >
                <SafeIcon icon={FiEdit} className="w-4 h-4" />
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => onDelete(prompt.id)}
                className="p-2 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                <SafeIcon icon={FiTrash2} className="w-4 h-4" />
              </motion.button>
            </div>
          </div>
          
          <div className="bg-slate-50 rounded-lg p-4">
            <p className="text-sm text-slate-700 line-clamp-3">
              {prompt.prompt.substring(0, 200)}...
            </p>
          </div>
          
          <div className="flex flex-wrap gap-2 mt-4">
            <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
              {prompt.data.category}
            </span>
            <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
              {prompt.data.projectType}
            </span>
            <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full">
              {prompt.data.complexity}
            </span>
          </div>
        </motion.div>
      ))}
    </div>
  );
};

export default SavedPrompts;